import { useContext, useMemo } from 'react';
import AppliedFiltersContext from '~/contexts/AppliedFiltersContext';
import SavedSearchesContext from '~/contexts/SavedSearchesContext';
import type { AppliedFilters, MakeType, ModelType } from '~/contracts';
import compareArrays from '~/utilities/compareArrays';
import { decodeAndParse } from '~/utilities/crypto';

export type FilterValue = string | number | boolean;

type MakeOrModel = MakeType | ModelType;
type FlatMakes = (string | string[])[];

/**
 * reduces the makes filter into a single array:
 * ["Honda", "Civic", "Trim_1", "Trim_2", "Toyota", "Camry"]
 */
const extractMakesModelsTrims = (items: MakeOrModel[]): FlatMakes => {
  return items.reduce((acc: FlatMakes, m: MakeOrModel) => {
    acc.push(m.name);

    if ('models' in m && m.models) {
      acc.push(...extractMakesModelsTrims(m.models));
    }

    if ('trims' in m && m.trims) {
      acc.push(...m.trims);
    }

    return acc;
  }, []);
};

const flattenFunctions = {
  makes: extractMakesModelsTrims,
  finance: Object.values,
  mileage: Object.values,
  mpg: Object.values,
  price: Object.values,
  year: Object.values
};

/**
 * Flattens all filters into an array that can be compared
 * Range filters are flattened with Object.values
 * Makes is flattened using extractMakesModelsTrims
 */
function flattenFilters(filters: AppliedFilters): FilterValue[] {
  return Object.entries(filters).flatMap(([key, values]) => {
    const flatten = flattenFunctions[key];

    if (!flatten) {
      return values ?? [];
    }

    return flatten(values);
  });
}

// determines if any savedSearches' filters match the current filters
const isCurrentSearchSaved =
  (appliedFilters: AppliedFilters) =>
  ({ url }): boolean => {
    const savedFilters: AppliedFilters = decodeAndParse(url).filters ?? {};

    // flattening all values into a single array for comparing
    const appliedFiltersToCompare = flattenFilters(appliedFilters);
    const savedFiltersToCompare = flattenFilters(savedFilters);

    return compareArrays(appliedFiltersToCompare, savedFiltersToCompare);
  };

function useSavedSearchApplied(): boolean {
  const { appliedFilters } = useContext(AppliedFiltersContext);
  const { savedSearches } = useContext(SavedSearchesContext);

  return useMemo(
    () => savedSearches.some(isCurrentSearchSaved(appliedFilters)),
    [appliedFilters, savedSearches]
  );
}

export default useSavedSearchApplied;
